// Pure helpers for the Overview tab: the video summary (GET /scenes `summary`) joined with the scene model and the
// project's characters, so renames and track edits from the Tracks tab show up here too.
import { isOther, isUnnamed } from "../tracks/tracksModel";

const p2 = (n) => String(n).padStart(2, "0");
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const hasId = (id) => id !== null && id !== undefined;

export const FORMATS = {
  scripted_drama: "Scripted drama",
  scripted_comedy: "Scripted comedy",
  feature_film: "Feature film",
  documentary: "Documentary",
  factual_entertainment: "Factual entertainment",
  reality: "Reality",
  news: "News",
  talk_show: "Talk show",
  game_show: "Game show",
  sport: "Sport",
  live_event: "Live event",
  presentation: "Presentation",
  commercial: "Commercial",
  other: "Other",
};
export const formatLabel = (f) => FORMATS[f] || cap(String(f || "").replace(/_/g, " "));

export const WEIGHT_LABEL = { A: "Main plot", B: "Secondary plot", C: "Minor thread" };
export const weightLabel = (w) => WEIGHT_LABEL[w] || WEIGHT_LABEL.C;

export const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** the summary with every field present, or null when the video has none (older videos) */
export function summaryOf(data) {
  const s = data && data.summary;
  if (!s) return null;
  return { format: "", genre: "", logline: "", synopsis: "", settings: [], topics: [], characters: [], key_moments: [], ...s };
}

/** a project character with a face track (voices have rep_track_id -1 and no tracks) */
export const hasTrack = (c) => !!c && c.rep_track_id !== -1 && (c.tracks_data || []).length > 0;

const byCharId = (items) => {
  const m = {};
  (items || []).forEach((c) => (m[String(c.char_id)] = c));
  return m;
};

/** content tags with the number of scenes that carry them, most scenes first */
export function tagCounts(model) {
  const n = {},
    order = [];
  (model ? model.scenes : []).forEach((x) =>
    x.tags.forEach((g) => {
      if (!(g in n)) {
        n[g] = 0;
        order.push(g);
      }
      n[g]++;
    })
  );
  return order.map((tag) => ({ tag, count: n[tag] })).sort((a, b) => b.count - a.count);
}

/**
 * The important characters [{id, name, role, track, screen, speech}]: the summary's, most important first, leaving
 * out the ones without a face track (no cluster id, or no tracks left after edits in the Tracks tab). Without a
 * summary: the characters with a face track, most screen time first, at most 10.
 * @param summary summaryOf(data)
 * @param items redux characters.items
 * @param times castTimes(items, sentences): char_id -> {screen, speech}
 */
export function importantCast(summary, items, times) {
  const ids = byCharId(items);
  const row = (c, role) => {
    const tm = (times || {})[c.char_id] || {};
    return { id: c.char_id, name: c.char_name, role: role || "", track: c.rep_track_id, screen: tm.screen || 0, speech: tm.speech || 0 };
  };
  if (summary && summary.characters.length) {
    const seen = new Set();
    const out = [];
    summary.characters.forEach((s) => {
      const c = hasId(s.id) ? ids[String(s.id)] : null;
      if (!hasTrack(c) || seen.has(c.char_id)) return;
      seen.add(c.char_id);
      out.push(row(c, s.role));
    });
    return out;
  }
  return (items || [])
    .filter(hasTrack)
    .map((c) => row(c, ""))
    .sort((a, b) => b.screen - a.screen)
    .slice(0, 10);
}

/**
 * The key moments [{k, s, e, note, scene, faces}] with their scene in the model (null when it isn't there) and the
 * faces of that scene's central characters [{name, track}] (track null: no thumbnail, show initials), at most 3.
 * @param model buildModel(...)
 * @param items redux characters.items
 */
export function keyMoments(summary, model, items) {
  const ids = byCharId(items);
  const pos = {};
  (model ? model.scenes : []).forEach((x) => (pos[x.index] = x.i));
  return ((summary && summary.key_moments) || []).map((m, k) => {
    const x = pos[m.scene_index] === undefined ? null : model.scenes[pos[m.scene_index]];
    const faces = [];
    (x ? x.characters : []).forEach((c) => {
      if (c.role !== "central" || !hasId(c.id) || faces.length >= 3 || faces.some((f) => f.name === c.name)) return;
      const item = ids[String(c.id)];
      faces.push({ name: c.name, track: hasTrack(item) ? item.rep_track_id : null });
    });
    return { k, s: m.start_time, e: m.end_time, note: m.note || "", scene: x, faces };
  });
}

/** index of the key moment playing at t, else -1 */
export const momentAt = (moments, t) => (moments || []).findIndex((m) => t >= m.s && t < m.e);

// always H:MM:SS: spreadsheets read "12:34" as 12 hours 34 minutes
const hms = (s) => {
  s = Math.max(0, Math.floor(Number(s) || 0));
  return Math.floor(s / 3600) + ":" + p2(Math.floor(s / 60) % 60) + ":" + p2(s % 60);
};

/**
 * Cast report CSV: every named character seen or heard, most screen time first, with the times the tab shows.
 * Unnamed characters ("Character 5") and the Others bucket are left out.
 * @param actor redux characters.actor (char_id -> actor name)
 * @param duration the video's length in seconds, for the share of runtime
 * @param t i18next t, for the column titles
 */
export function castReportCsv(items, times, actor, duration, t) {
  const tr = t || ((s) => s);
  const rows = [[tr("Character"), tr("Actor"), tr("Screen Time"), tr("Speech Time"), tr("Share of Runtime")]];
  (items || [])
    .filter((c) => !isOther(c) && !isUnnamed(c.char_name))
    .map((c) => ({ c, tm: (times || {})[c.char_id] || { screen: 0, speech: 0 } }))
    .filter(({ tm }) => tm.screen > 0 || tm.speech > 0)
    .sort((a, b) => b.tm.screen - a.tm.screen || b.tm.speech - a.tm.speech)
    .forEach(({ c, tm }) =>
      rows.push([
        c.char_name,
        (actor || {})[c.char_id] || c.char_actor || "",
        hms(tm.screen),
        hms(tm.speech),
        duration > 0 ? ((tm.screen / duration) * 100).toFixed(1) + "%" : "-",
      ])
    );
  return rows.map((r) => r.map((v) => '"' + String(v === null || v === undefined ? "" : v).replace(/"/g, '""') + '"').join(",")).join("\r\n");
}
